import React, { useEffect } from "react";
import { createContext, useCallback, useMemo, useState } from "react";
import { getItem, setItem } from "../utils/StorageHelpers";
import { useTranslation } from "react-i18next";
import { OktaConstructor } from "../config/okta.config";
import LogCapture from "../utils/LogManagement/LogHelper";

// Google Anayltics
import ReactGA from "react-ga";

import {
    firebaseSignInWithCustomToken,
    getFirebaseToken,
    onMessageListener,
    onNotificationsSnapshotListener,
} from "../utils/FirebaseHelper";
import { isEmpty } from "lodash";
import { fetchLoggedUser } from "../utils/UserHelper";
import * as Sentry from "@sentry/react";

const UserContext = createContext({ user: undefined });

const UserContextProvider = ({ children }) => {
    const TRACKING_ID = process.env.REACT_APP_TRACKING_ID || "UA-210560378-1";
    const [googleAnalyticsInitialized, setGoogleAnalyticsInitialized] =
        useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const [state, setState] = useState(() => JSON.parse(getItem("user")) || {});
    const { i18n } = useTranslation();
    const { logoutRedirectUri, oktaClient, oktaConfig } = OktaConstructor();

    const updateState = useCallback(
        (newValue) => {
            setState(newValue);
            setItem("user", newValue);
        },
        [setState]
    );

    useEffect(() => {
        if (isEmpty(state)) {
            setGoogleAnalyticsInitialized(false);
        }
    }, [state]);

    const changeLanguage = (lng) => {
        if (i18n.language !== lng) {
            i18n.changeLanguage(lng);
        }
    };

    const initUtils = async () => {
        const isAuthenticated = await oktaClient.isAuthenticated();
        if (isAuthenticated) {
            const _user = await fetchLoggedUser();
            updateState(_user);
            initReactGA(_user);
            initFirebase(_user);
        }
    };

    const initFirebase = async (user) => {
        const email = user?.email || "unkown";
        try {
            await firebaseSignInWithCustomToken(email);
            await getFirebaseToken();

            onMessageListener().catch((error) =>
                LogCapture.error(
                    `Failed to initialize notification center user email: ${email} error: ${error}`
                )
            );

            onNotificationsSnapshotListener();
        } catch (error) {
            LogCapture.error(
                `Failed to initialize notification center user email: ${email} error: ${error}`
            );
        }
    };

    const initReactGA = (user) => {
        Sentry.setUser({ email: user?.email || "Unknow" });

        ReactGA.initialize(TRACKING_ID, {
            gaOptions: {
                userId: user?.id || "Unknown",
                user: user?.email || "Unknown",
            },
            cookieDomain: "auto",
        });

        setGoogleAnalyticsInitialized(true);
    };

    const contextValue = useMemo(
        () => ({
            user: state,
            setUser: updateState,
            changeLanguage,
            logoutRedirectUri,
            oktaClient,
            oktaConfig,
            initUtils,
            googleAnalyticsInitialized,
            isLoggingOut,
            setIsLoggingOut,
        }),
        [
            state,
            updateState,
            changeLanguage,
            logoutRedirectUri,
            oktaClient,
            oktaConfig,
            initUtils,
            googleAnalyticsInitialized,
            isLoggingOut,
            setIsLoggingOut,
        ]
    );

    return (
        <UserContext.Provider value={contextValue}>
            {children}
        </UserContext.Provider>
    );
};

export { UserContext, UserContextProvider };
